import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { Delete, PersonAdd } from '@mui/icons-material';
import type { ThinkSpace, SharePermission } from '@/types/thinkspace';
import { useInviteUser, useUpdatePermission, useRemoveSharedUser } from '@/hooks';

type ShareDialogProps = {
  open: boolean;
  onClose: () => void;
  thinkSpace: ThinkSpace;
};

export const ShareDialog = ({ open, onClose, thinkSpace }: ShareDialogProps) => {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<SharePermission>('read');
  const [error, setError] = useState('');

  const inviteMutation = useInviteUser();
  const updatePermissionMutation = useUpdatePermission();
  const removeMutation = useRemoveSharedUser();

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Maximum number of users that can share a ThinkSpace (Firestore rules limit)
  const MAX_SHARED_USERS = 20;

  const handleInvite = async () => {
    setError('');

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (email.toLowerCase() === thinkSpace.ownerEmail.toLowerCase()) {
      setError('You cannot invite yourself');
      return;
    }

    if (thinkSpace.sharedWith.some((s) => s.email.toLowerCase() === email.toLowerCase())) {
      setError('This user has already been invited');
      return;
    }

    if (thinkSpace.sharedWith.length >= MAX_SHARED_USERS) {
      setError(`This ThinkSpace has reached the maximum of ${MAX_SHARED_USERS} shared users`);
      return;
    }

    try {
      await inviteMutation.mutateAsync({
        thinkSpaceId: thinkSpace.id,
        input: { email: email.toLowerCase(), permission },
      });
      setEmail('');
    } catch (err) {
      setError('Failed to send invitation');
    }
  };

  const handlePermissionChange = async (userEmail: string, newPermission: SharePermission) => {
    try {
      await updatePermissionMutation.mutateAsync({
        thinkSpaceId: thinkSpace.id,
        userEmail,
        permission: newPermission,
      });
    } catch (err) {
      console.error('Failed to update permission:', err);
    }
  };

  const handleRemove = async (userEmail: string) => {
    try {
      await removeMutation.mutateAsync({
        thinkSpaceId: thinkSpace.id,
        userEmail,
      });
    } catch (err) {
      console.error('Failed to remove user:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share "{thinkSpace.name}"</DialogTitle>
      <DialogContent>
        {/* Invite Form */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, mt: 1 }}>
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Access</InputLabel>
            <Select
              value={permission}
              label="Access"
              onChange={(e) => setPermission(e.target.value as SharePermission)}
            >
              <MenuItem value="read">View</MenuItem>
              <MenuItem value="write">Edit</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviteMutation.isPending}
            startIcon={<PersonAdd />}
          >
            Invite
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Owner */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Owner
        </Typography>
        <ListItem sx={{ px: 0 }}>
          <ListItemAvatar>
            <Avatar>{thinkSpace.ownerName?.[0] || thinkSpace.ownerEmail[0]}</Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={thinkSpace.ownerName || thinkSpace.ownerEmail}
            secondary={thinkSpace.ownerEmail}
          />
        </ListItem>

        {/* Shared Users */}
        {thinkSpace.sharedWith.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Shared with ({thinkSpace.sharedWith.length})
            </Typography>
            <List dense disablePadding>
              {thinkSpace.sharedWith.map((share) => (
                <ListItem key={share.id} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar src={share.photoURL || undefined}>
                      {share.displayName?.[0] || share.email[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {share.displayName || share.email}
                        <Chip
                          label={share.status}
                          size="small"
                          color={getStatusColor(share.status) as any}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={share.displayName ? share.email : undefined}
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" variant="standard">
                      <Select
                        value={share.permission}
                        onChange={(e) =>
                          handlePermissionChange(share.email, e.target.value as SharePermission)
                        }
                        disabled={updatePermissionMutation.isPending}
                      >
                        <MenuItem value="read">View</MenuItem>
                        <MenuItem value="write">Edit</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemove(share.email)}
                      disabled={removeMutation.isPending}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {thinkSpace.sharedWith.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Not shared with anyone yet
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};
